import type { BlockDocument, BlockNode } from '../../cms/editor/document';
import { blockMeta } from '../../cms/editor/blockMeta';
import { canDelete, findParent } from '../../cms/editor/document';

/**
 * The document outline.
 *
 * ── THE TREE IS THE DOCUMENT, NOT A VIEW OF THE PAGE ─────────────────────────────────
 *
 * Composition is a tree, so the control for it is a tree. Dragging blocks around a canvas
 * reads well in a demo and hides exactly what an admin needs to see here: which container
 * a block is in, and what happens when it moves. Every operation is a named, reversible
 * action with a reason when it is refused.
 *
 * ── A REFUSAL IS PART OF THE CONTROL ─────────────────────────────────────────────────
 *
 * The delete button for the only ticket panel is disabled and says why on hover, rather
 * than being absent (which reads as a bug) or enabled and then refused (which wastes the
 * click and teaches nothing).
 */
export function Outline({
  document, selectedNodeId, onSelect, onMove, onRemove, onDuplicate, problemNodeIds,
  sectionNames,
}: {
  document: BlockDocument;
  selectedNodeId: string | null;
  onSelect: (nodeId: string) => void;
  onMove: (nodeId: string, direction: 'up' | 'down') => void;
  onRemove: (nodeId: string) => void;
  onDuplicate: (nodeId: string) => void;
  problemNodeIds: Set<string>;
  /** §64: id -> name, so a reference reads as what it points at. */
  sectionNames?: Record<string, string>;
}) {
  const render = (node: BlockNode, depth: number) => {
    const meta = blockMeta(node.component);
    const parent = findParent(document, node.id);
    const siblings = parent?.children ?? [];
    const index = siblings.findIndex((c) => c.id === node.id);
    const verdict = canDelete(document, node.id);
    const isRoot = document.root?.id === node.id;

    // §64: "Global Section" tells nobody anything. "Global Section — Main Footer" says
    // which reusable thing this page is pointing at, which is the only reason the row is
    // interesting. An unresolved reference reads as such rather than silently as a bare
    // block name.
    const referenced = node.component === 'GlobalSection'
      ? (node.props?.globalSectionId as string | null) ?? null
      : null;
    const referencedName = referenced
      ? (sectionNames?.[referenced] ?? 'unknown section')
      : null;
    const displayName = referencedName
      ? `${meta?.label ?? node.component} — ${referencedName}`
      : (meta?.label ?? node.component);

    return (
      <li key={node.id}>
        <div
          className={`cms-outline__row${selectedNodeId === node.id ? ' is-selected' : ''}`}
          style={{ paddingLeft: `${depth * 14 + 8}px` }}
        >
          <button type="button" className="cms-outline__label" onClick={() => onSelect(node.id)}>
            <span className="cms-outline__name">{displayName}</span>
            {node.component === 'GlobalSection' && !referenced && (
              <span className="badge badge--pending" title="This block renders nothing until a section is chosen.">
                Empty
              </span>
            )}
            {meta?.critical && <span className="badge badge--ok" title="Required to sell tickets">Required</span>}
            {!meta && <span className="badge badge--pending">Unknown block</span>}
            {problemNodeIds.has(node.id) && <span className="badge badge--error">Problem</span>}
            <span className="cms-outline__id">{node.id}</span>
          </button>

          <span className="cms-outline__tools">
            <button type="button" className="cms-icon" title="Move up" aria-label={`Move ${meta?.label ?? node.component} up`}
              disabled={isRoot || index <= 0} onClick={() => onMove(node.id, 'up')}>↑</button>
            <button type="button" className="cms-icon" title="Move down" aria-label={`Move ${meta?.label ?? node.component} down`}
              disabled={isRoot || index < 0 || index >= siblings.length - 1}
              onClick={() => onMove(node.id, 'down')}>↓</button>
            <button type="button" className="cms-icon" title="Duplicate" aria-label={`Duplicate ${meta?.label ?? node.component}`}
              disabled={isRoot} onClick={() => onDuplicate(node.id)}>⧉</button>
            <button type="button" className="cms-icon cms-icon--danger"
              title={verdict.allowed ? 'Remove' : verdict.message}
              aria-label={`Remove ${meta?.label ?? node.component}`}
              disabled={!verdict.allowed} onClick={() => onRemove(node.id)}>×</button>
          </span>
        </div>

        {(node.children ?? []).length > 0 && (
          <ul className="cms-outline__children">
            {(node.children ?? []).map((child) => render(child, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  if (!document?.root) return <p className="adm-muted">This draft has no content yet.</p>;

  return <ul className="cms-outline">{render(document.root, 0)}</ul>;
}
